import { addCalendarDays } from "@common/helpers/date";
import { splitAmountByAllocationPercents } from "@common/constants/domains/accounting/schedules";
import { ScheduleTemplateResponse } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    RecognitionAnchor,
    RecognitionMethod,
} from "@common/types/domains/accounting/schedule";

export enum ManualRecognitionDateSource {
    termStart = "termStart",
    specificDate = "specificDate",
    derived = "derived",
}

export interface ManualRecognitionRow {
    id: string;
    method: RecognitionMethod;
    amount: number;
    dateSource: ManualRecognitionDateSource;
    /** The resolved recognition date, or "" until its anchor date is set. */
    date: string;
}

/** An anchor date moved by an offset, or "" while the anchor is unset. */
export function offsetDate(date: string, offsetDays: number): string {
    return date ? addCalendarDays(date, offsetDays) : "";
}

function resolveRowDate(options: {
    anchor: RecognitionAnchor;
    offsetDays: number;
    termStart: string;
    specificDate: string;
}): Pick<ManualRecognitionRow, "dateSource" | "date"> {
    const isSpecificDate = options.anchor === RecognitionAnchor.specificDate;
    const anchorDate = isSpecificDate
        ? options.specificDate
        : options.termStart;
    if (options.offsetDays !== 0) {
        return {
            dateSource: ManualRecognitionDateSource.derived,
            date: offsetDate(anchorDate, options.offsetDays),
        };
    }
    return {
        dateSource: isSpecificDate
            ? ManualRecognitionDateSource.specificDate
            : ManualRecognitionDateSource.termStart,
        date: anchorDate,
    };
}

export function buildManualRecognitionRows(options: {
    components: ScheduleTemplateResponse["components"];
    amount: number;
    termStart: string;
    specificDate: string;
}): ManualRecognitionRow[] {
    const components = [...options.components].sort(
        (left, right) => left.order - right.order,
    );
    const amounts = splitAmountByAllocationPercents(
        options.amount,
        components.map((component) => component.allocationPercent),
    );
    return components.map((component, index) => ({
        id: component.id,
        method: component.method,
        amount: amounts[index],
        ...resolveRowDate({
            anchor: component.anchor,
            offsetDays: component.offsetDays,
            termStart: options.termStart,
            specificDate: options.specificDate,
        }),
    }));
}

export function resolvePolicyAccountIds(options: {
    accountRows: ScheduleTemplateResponse["accountRows"];
    entityId: number | null;
}): { holdingAccountId: string; recognitionAccountId: string } | null {
    const accountRow = options.accountRows.find(
        (row) =>
            row.entityId === options.entityId &&
            row.basis === AccountingLedgerBasis.accruals,
    );
    return accountRow
        ? {
              holdingAccountId: accountRow.holdingAccountId,
              recognitionAccountId: accountRow.recognitionAccountId,
          }
        : null;
}
